/**
 * Persist pre-contract products + utility details into oneBUTTON APIs.
 */
import designServices from '../../services/oneButtonServices/designServices';
import opportunityInfoService from '../../services/oneButtonServices/opportunityInfoService';
import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';
import { PRE_CONTRACT_PRODUCTS } from './preContractTypes';

export type PreContractSaveOneButtonResult = {
    ok: boolean;
    message: string;
};

function parseUsage(value: string): number | null {
    const n = Number(String(value).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function parseRate(value: string): number | null {
    const n = Number(String(value).trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

function productActive(
    products: PreContractIntakeForm['products'],
    productTypeName: string,
): boolean | null {
    const key = PRE_CONTRACT_PRODUCTS.find(
        (p) => p.toLowerCase() === productTypeName.trim().toLowerCase(),
    ) as PreContractProduct | undefined;
    if (!key) return null;
    return Boolean(products[key]);
}

function buildOpportunityProductPayload(
    opportunityId: number,
    rows: any[],
    products: PreContractIntakeForm['products'],
): Array<{
    opportunity_Id: number;
    product_Type_Name: string;
    product_Type_Id: number;
    finance_Partner_Id: number;
    activeFlag: boolean;
    opportunity_Product_Id: number;
    finance_Partner: string;
    finance_Partner_Display_Name: string;
    finance_Method_Name: string;
}> {
    return rows
        .map((row) => {
            const productTypeName = String(
                row?.Product_Type_Name ?? row?.product_Type_Name ?? '',
            ).trim();
            if (!productTypeName) return null;
            const wanted = productActive(products, productTypeName);
            const currentlyActive =
                row?.ActiveFlag !== false && row?.activeFlag !== false;
            const activeFlag = wanted == null ? currentlyActive : wanted;

            return {
                opportunity_Id: opportunityId,
                product_Type_Name: productTypeName,
                product_Type_Id: Number(row?.Product_Type_Id ?? row?.product_Type_Id ?? 0) || 0,
                finance_Partner_Id: Number(row?.Finance_Partner_Id ?? row?.finance_Partner_Id ?? 0) || 0,
                activeFlag,
                opportunity_Product_Id:
                    Number(row?.Opportunity_Product_Id ?? row?.opportunity_Product_Id ?? 0) || 0,
                finance_Partner: String(row?.Finance_Partner ?? row?.finance_Partner ?? ''),
                finance_Partner_Display_Name: String(
                    row?.Finance_Partner_Display_Name ??
                        row?.finance_Partner_Display_Name ??
                        row?.Finance_Partner ??
                        '',
                ),
                finance_Method_Name: String(
                    row?.Finance_Method_Name ?? row?.finance_Method_Name ?? '',
                ),
            };
        })
        .filter((row): row is NonNullable<typeof row> => row != null);
}

/** Save utility company / usage / rate and product active flags to oneBUTTON. */
export async function savePreContractOneButtonSelection(
    salesForceId: string,
    form: PreContractIntakeForm,
): Promise<PreContractSaveOneButtonResult> {
    const sfid = salesForceId.trim();
    if (!sfid) {
        return { ok: false, message: 'Missing Salesforce opportunity id.' };
    }

    try {
        const detailRaw = await opportunityInfoService.getOpportunityDetail(sfid);
        const opportunity = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
        const opportunityIdRaw = opportunity?.Opportunity_Id;
        const opportunityId = Number(opportunityIdRaw);
        if (!Number.isFinite(opportunityId) || opportunityId <= 0) {
            return { ok: false, message: 'Opportunity is not loaded in oneBUTTON yet.' };
        }

        const utilityResult = await designServices.RetrieveUtilityInformation(opportunityId);
        const utilityDetails = utilityResult?.utilityDetails;
        if (!utilityDetails?.SolarUtility) {
            return {
                ok: false,
                message: 'Utility details are not available to save for this opportunity.',
            };
        }

        const annualUsage = parseUsage(form.annualUsageKwh);
        const utilityRate = parseRate(form.utilityRate);
        const company = form.utilityCompany.trim();

        const solarUtility = {
            ...utilityDetails.SolarUtility,
            ...(company
                ? {
                      UtilityCompanyName: company,
                      UtilityCompany: company,
                  }
                : {}),
            ...(annualUsage != null
                ? {
                      AnnualUsage: annualUsage,
                      EnterAnnualUsage: 'AnnualUsageCont',
                  }
                : {}),
            ...(utilityRate != null
                ? {
                      CurrentAllInUtilityRate: utilityRate,
                      Utility_Rate: utilityRate,
                  }
                : {}),
        };

        await designServices.SaveProductConfigUtility({
            ...utilityDetails,
            SolarUtility: solarUtility,
        });

        const opportunityProductRows = Array.isArray(opportunity?.OpportunityProduct)
            ? opportunity.OpportunityProduct
            : Array.isArray(opportunity?.opportunityProduct)
              ? opportunity.opportunityProduct
              : [];

        if (opportunityProductRows.length > 0) {
            const opportunityProduct = buildOpportunityProductPayload(
                opportunityId,
                opportunityProductRows,
                form.products,
            );
            if (opportunityProduct.length > 0) {
                await opportunityInfoService.updateOpportunityProduct({
                    opportunityId,
                    salesForce_Id: sfid,
                    opportunityProduct,
                });
            }
        }

        return { ok: true, message: 'Products and utility details saved to oneBUTTON.' };
    } catch (error) {
        const message =
            error instanceof Error && error.message.trim()
                ? error.message
                : 'Failed to save products and utility to oneBUTTON.';
        return { ok: false, message };
    }
}
